import { fixed, trim } from './format';
import { require, requirePositive, step, type Solved } from './types';

/** Densities in pounds per cubic inch — the unit stock is bought and quoted in. */
export const DENSITIES = {
  'mild steel': 0.284,
  'stainless steel': 0.29,
  'cast iron': 0.26,
  aluminium: 0.098,
  brass: 0.307,
  bronze: 0.32,
  copper: 0.323,
  titanium: 0.163,
  'grey plastic (UHMW)': 0.034,
} as const;

export type Material = keyof typeof DENSITIES;

export function circleArea(diameter: number): Solved<number> {
  requirePositive(diameter, 'diameter');
  const radius = diameter / 2;
  const value = Math.PI * radius * radius;
  return {
    value,
    steps: [
      step('formula', 'area = π × r²'),
      step('compute', `r = ${trim(diameter)} ÷ 2 = ${trim(radius)}`),
      step('substitute', `π × ${trim(radius)}² = π × ${fixed(radius * radius, 4)}`),
      step('result', `= ${fixed(value, 4)} square inches`),
      step('note', 'Radius, not diameter. Halving first is the step everyone skips.'),
    ],
  };
}

export function rectangleArea(width: number, height: number): Solved<number> {
  requirePositive(width, 'width');
  requirePositive(height, 'height');
  const value = width * height;
  return {
    value,
    steps: [
      step('formula', 'area = width × height'),
      step('substitute', `${trim(width)} × ${trim(height)}`),
      step('result', `= ${fixed(value, 4)} square inches`),
    ],
  };
}

export function cylinderVolume(diameter: number, length: number): Solved<number> {
  requirePositive(diameter, 'diameter');
  requirePositive(length, 'length');
  const area = circleArea(diameter);
  const value = area.value * length;
  return {
    value,
    steps: [
      step('formula', 'volume = area of the end × length'),
      ...area.steps.slice(0, 4),
      step('substitute', `${fixed(area.value, 4)} × ${trim(length)}`),
      step('result', `= ${fixed(value, 4)} cubic inches`),
    ],
  };
}

export function blockVolume(width: number, height: number, length: number): Solved<number> {
  requirePositive(width, 'width');
  requirePositive(height, 'height');
  requirePositive(length, 'length');
  const value = width * height * length;
  return {
    value,
    steps: [
      step('formula', 'volume = width × height × length'),
      step('substitute', `${trim(width)} × ${trim(height)} × ${trim(length)}`),
      step('result', `= ${fixed(value, 4)} cubic inches`),
    ],
  };
}

export function weight(volumeCubicInches: number, material: Material): Solved<number> {
  requirePositive(volumeCubicInches, 'volume');
  const density = DENSITIES[material];
  require(density !== undefined, `Unknown material: ${material}`);
  const value = volumeCubicInches * density;
  return {
    value,
    steps: [
      step('formula', 'weight = volume × density'),
      step('compute', `${material} is ${density} lb per cubic inch`),
      step('substitute', `${fixed(volumeCubicInches, 4)} × ${density}`),
      step('result', `= ${fixed(value, 3)} lb`),
      step(
        'note',
        'Steel is roughly three times the weight of aluminium for the same lump. Useful sanity check when a number looks wrong.',
      ),
    ],
  };
}

// ── Ratios and gearing ───────────────────────────────────────────────────────

/** Solve a proportion a : b = c : x for the missing term. */
export function proportion(a: number, b: number, c: number): Solved<number> {
  requirePositive(a, 'first term');
  requirePositive(b, 'second term');
  requirePositive(c, 'third term');
  const value = (b * c) / a;
  return {
    value,
    steps: [
      step('formula', `${trim(a)} is to ${trim(b)} as ${trim(c)} is to what?`),
      step('substitute', `Cross-multiply: (${trim(b)} × ${trim(c)}) ÷ ${trim(a)}`),
      step('compute', `${trim(b)} × ${trim(c)} = ${fixed(b * c, 4)}`),
      step('result', `= ${fixed(value, 4)}`),
      step('note', 'Keep the units in the same place on both sides or the answer comes out upside down.'),
    ],
  };
}

function gcd(a: number, b: number): number {
  return b === 0 ? a : gcd(b, a % b);
}

export interface Ratio {
  readonly a: number;
  readonly b: number;
  readonly decimal: number;
}

/** Gear ratio, reduced to whole numbers. */
export function gearRatio(driving: number, driven: number): Solved<Ratio> {
  require(Number.isInteger(driving) && driving > 0, 'Driving gear teeth must be a whole number');
  require(Number.isInteger(driven) && driven > 0, 'Driven gear teeth must be a whole number');

  const g = gcd(driving, driven);
  const value: Ratio = { a: driving / g, b: driven / g, decimal: driving / driven };

  return {
    value,
    steps: [
      step('formula', 'ratio = driving teeth : driven teeth'),
      step('substitute', `${driving} : ${driven}`),
      step('compute', `Both divide by ${g}`),
      step('result', `= ${value.a} : ${value.b} (${fixed(value.decimal, 4)})`),
      step(
        'note',
        value.decimal < 1
          ? 'Driven gear is bigger, so this is a reduction — slower, with more torque.'
          : value.decimal > 1
            ? 'Driving gear is bigger, so this speeds the output up.'
            : 'One to one. Same speed straight through.',
      ),
    ],
  };
}
